import { useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Boxes, Plus, Pencil, Trash2, X, MapPin } from "lucide-react";

// Warehouse Groups foundation (Steps 1/2 only). An owner-defined assessment
// unit that groups one or more sites sharing one common answer set. This does
// NOT touch Step 3 / scoring / reports (Phase 2).

const SCENARIOS = [
  { value: "greenfield", label: "Greenfield" },
  { value: "brownfield", label: "Brownfield" },
  { value: "cloud_migration", label: "Cloud Migration" },
];
const SCENARIO_LABEL = Object.fromEntries(SCENARIOS.map((s) => [s.value, s.label]));

// Current Warehouse Solution + Preserve Architecture option lists are passed in
// from the Admin question bank (current_sap_solution / preserve_architecture_
// importance) — NOT hardcoded here — so they never drift from Admin. The value
// strings still match the bank values ("SAP EWM …", "Other WMS") used below.

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `wg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const emptyGroup = (defaultScenario) => ({
  id: uid(),
  name: "",
  sites: [],
  warehouse_count: "",
  warehouse_types: [],
  current_solution: "",
  current_solution_name: "",
  current_version: "",
  assessment_type: defaultScenario || "",
  context: { preserve_architecture_importance: "" },
});

const isEwm = (sol) => (sol || "").startsWith("SAP EWM");
const isOther = (sol) => sol === "Other WMS";
const needsArch = (scenario) => scenario === "brownfield" || scenario === "cloud_migration";

// A Current Solution Version applies to real SAP solutions and Other WMS, but
// not to a "Not Applicable / Non-SAP / Greenfield" answer. Reuses the existing
// current_version field — no new version concept is introduced.
const showsVersion = (sol) => {
  if (!sol) return false;
  if (isOther(sol)) return true;
  if (/not applicable|non-sap|greenfield|none/i.test(sol)) return false;
  return /^SAP/i.test(sol);
};

// SmartEvaluate derives the group's internal transformation scenario — the
// client is never asked to choose it. Greenfield/Brownfield/Cloud Migration
// top-level assessments propagate to every group; Mixed Landscape derives per
// group from the factual Current Warehouse Solution: a greenfield / non-SAP
// answer (Other WMS, or "Not Applicable (Non-SAP / Greenfield)") → greenfield,
// every other (real existing SAP/other WMS system) → brownfield. Cloud
// Migration is never derived inside a Mixed Landscape group.
const isGreenfieldSolution = (sol) =>
  isOther(sol) || /not applicable|non-sap|greenfield|none/i.test(sol || "");

const deriveGroupScenario = (topScenario, currentSolution) => {
  if (topScenario === "mixed_landscape") {
    return isGreenfieldSolution(currentSolution) ? "greenfield" : "brownfield";
  }
  if (["greenfield", "brownfield", "cloud_migration"].includes(topScenario)) {
    return topScenario;
  }
  return "";
};

const INPUT_CLS =
  "rounded-sm h-11 text-sm border-[#D1D5DB] text-[#1F2937] focus:border-[#025979] focus:ring-[#025979]";

// --- Lightweight tag input for sites (persists as a string array) ----------
function SiteTagInput({ value, onChange, disabled }) {
  const [draft, setDraft] = useState("");
  const tags = Array.isArray(value) ? value : [];

  const commit = (raw) => {
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => !tags.some((t) => t.toLowerCase() === s.toLowerCase()));
    if (parts.length) onChange([...tags, ...parts]);
    setDraft("");
  };
  const remove = (t) => onChange(tags.filter((x) => x !== t));

  return (
    <div>
      <div
        className="flex flex-wrap items-center gap-1.5 rounded-sm border border-[#D1D5DB] bg-white p-2 min-h-11 focus-within:border-[#025979]"
        data-testid="group-sites-tags"
      >
        {tags.map((t) => (
          <span
            key={t}
            data-testid={`group-site-tag-${t}`}
            className="inline-flex items-center gap-1 rounded-full bg-[#E0F2FE] text-[#025979] border border-[#025979]/20 px-2 py-0.5 text-xs font-medium"
          >
            {t}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${t}`}
                data-testid={`group-site-remove-${t}`}
                onClick={() => remove(t)}
                className="hover:text-red-600"
              >
                <X className="w-3 h-3" strokeWidth={2.5} />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            data-testid="group-sites-input"
            value={draft}
            placeholder={tags.length ? "Add another…" : "e.g. UK, Italy, Spain"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                if (draft.trim()) commit(draft);
              } else if (e.key === "Backspace" && !draft && tags.length) {
                remove(tags[tags.length - 1]);
              }
            }}
            onBlur={() => draft.trim() && commit(draft)}
            className="flex-1 min-w-[120px] border-0 outline-none text-sm text-[#1F2937] placeholder:text-[#9CA3AF] bg-transparent"
          />
        )}
      </div>
      <p className="text-[11px] text-[#9CA3AF] mt-1">Press Enter or comma to add each site.</p>
    </div>
  );
}

// --- Group editor dialog ----------------------------------------------------
function GroupEditorDialog({ open, onOpenChange, initial, defaultScenario, topScenario, warehouseTypeOptions, solutionOptions, preserveArchOptions, onSave }) {
  const [g, setG] = useState(initial);
  const [errs, setErrs] = useState({});
  const isEdit = !!(initial && initial.name);

  const set = (k, v) => setG((p) => {
    const next = { ...p, [k]: v };
    // When the factual Current Warehouse Solution changes, re-derive the
    // internal scenario (Mixed Landscape) and drop now-inapplicable fields.
    if (k === "current_solution") {
      const derived = deriveGroupScenario(topScenario, v);
      if (derived) next.assessment_type = derived;
      if (!isOther(v)) next.current_solution_name = "";
      if (!showsVersion(v)) next.current_version = "";
    }
    return next;
  });
  const toggleType = (val) => {
    const arr = Array.isArray(g.warehouse_types) ? g.warehouse_types : [];
    set("warehouse_types", arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const validate = () => {
    const e = {};
    if (!g.name || !g.name.trim()) e.name = "Group name is required";
    if (!g.sites || g.sites.length === 0) e.sites = "Add at least one site";
    const n = Number(g.warehouse_count);
    if (g.warehouse_count === "" || Number.isNaN(n) || n < 1) e.warehouse_count = "Enter a valid number (min 1)";
    if (!g.warehouse_types || g.warehouse_types.length === 0) e.warehouse_types = "Select at least one type";
    if (!g.current_solution) e.current_solution = "Select the current solution";
    if (isOther(g.current_solution) && !(g.current_solution_name || "").trim()) e.current_solution_name = "Enter the system name";
    return e;
  };

  const save = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrs(e); return; }
    // Normalize: strip fields that don't apply to the chosen solution, and
    // derive the internal scenario from the top-level assessment / current
    // solution. context (historical preserve_architecture_importance) is
    // preserved rather than destructively cleaned.
    const clean = {
      ...g,
      name: g.name.trim(),
      warehouse_count: Number(g.warehouse_count),
      current_solution_name: isOther(g.current_solution) ? (g.current_solution_name || "").trim() : "",
      current_version: showsVersion(g.current_solution) ? (g.current_version || "").trim() : "",
      assessment_type: deriveGroupScenario(topScenario, g.current_solution) || g.assessment_type || "",
      context: g.context || {},
    };
    onSave(clean);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto rounded-sm" data-testid="group-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-base" style={{ fontFamily: "Chivo, sans-serif" }}>
            {isEdit ? "Edit Warehouse Group" : "Add Warehouse Group"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Define a group of warehouses that share one common set of assessment answers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* Group name */}
          <div>
            <Label className="text-xs font-medium text-[#1F2937] mb-1.5 block">Group Name <span className="text-red-500">*</span></Label>
            <Input
              data-testid="group-name-input"
              value={g.name}
              placeholder="e.g. European WM Sites"
              onChange={(e) => set("name", e.target.value)}
              className={`${INPUT_CLS} ${errs.name ? "border-red-400" : ""}`}
            />
            {errs.name && <p className="text-xs text-red-500 mt-1" data-testid="group-name-error">{errs.name}</p>}
          </div>

          {/* Sites */}
          <div>
            <Label className="text-xs font-medium text-[#1F2937] mb-1.5 block">Sites / Warehouses <span className="text-red-500">*</span></Label>
            <SiteTagInput value={g.sites} onChange={(v) => set("sites", v)} />
            {errs.sites && <p className="text-xs text-red-500 mt-1" data-testid="group-sites-error">{errs.sites}</p>}
          </div>

          {/* Number of warehouses */}
          <div className="max-w-[200px]">
            <Label className="text-xs font-medium text-[#1F2937] mb-1.5 block">Number of Warehouses <span className="text-red-500">*</span></Label>
            <Input
              data-testid="group-warehouse-count-input"
              type="number"
              min={1}
              value={g.warehouse_count}
              placeholder="e.g. 3"
              onChange={(e) => set("warehouse_count", e.target.value)}
              className={`${INPUT_CLS} ${errs.warehouse_count ? "border-red-400" : ""}`}
            />
            {errs.warehouse_count && <p className="text-xs text-red-500 mt-1" data-testid="group-count-error">{errs.warehouse_count}</p>}
          </div>

          {/* Warehouse types */}
          <div>
            <Label className="text-xs font-medium text-[#1F2937] mb-1.5 block">Warehouse Type(s) <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="group-warehouse-types">
              {(warehouseTypeOptions || []).map((o) => {
                const sel = (g.warehouse_types || []).includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={`group-type-${o.value}`}
                    data-state={sel ? "selected" : "unselected"}
                    onClick={() => toggleType(o.value)}
                    className={`text-left p-2.5 rounded-sm border text-sm transition-colors ${
                      sel ? "border-[#025979] bg-[#E0F2FE]/50 text-[#111827]" : "border-[#E5E7EB] hover:bg-gray-50 text-[#374151]"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            {errs.warehouse_types && <p className="text-xs text-red-500 mt-1" data-testid="group-types-error">{errs.warehouse_types}</p>}
          </div>

          {/* Current solution */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-[#1F2937] mb-1.5 block">Current Warehouse Solution <span className="text-red-500">*</span></Label>
              <Select value={g.current_solution} onValueChange={(v) => set("current_solution", v)}>
                <SelectTrigger data-testid="group-solution-select" className={`${INPUT_CLS} ${errs.current_solution ? "border-red-400" : ""}`}>
                  <SelectValue placeholder="Select solution" />
                </SelectTrigger>
                <SelectContent>
                  {(solutionOptions || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {errs.current_solution && <p className="text-xs text-red-500 mt-1" data-testid="group-solution-error">{errs.current_solution}</p>}
            </div>

            {isOther(g.current_solution) && (
              <div>
                <Label className="text-xs font-medium text-[#1F2937] mb-1.5 block">System Name <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="group-solution-name-input"
                  value={g.current_solution_name}
                  placeholder="e.g. JDE"
                  onChange={(e) => set("current_solution_name", e.target.value)}
                  className={`${INPUT_CLS} ${errs.current_solution_name ? "border-red-400" : ""}`}
                />
                {errs.current_solution_name && <p className="text-xs text-red-500 mt-1" data-testid="group-solution-name-error">{errs.current_solution_name}</p>}
              </div>
            )}

            {showsVersion(g.current_solution) && (
              <div>
                <Label className="text-xs font-medium text-[#1F2937] mb-1.5 block">Current Solution Version <span className="text-[#6B7280] font-normal">(optional)</span></Label>
                <Input
                  data-testid="group-version-input"
                  value={g.current_version}
                  placeholder="e.g. 9.2 / 2023"
                  onChange={(e) => set("current_version", e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" data-testid="group-cancel-btn" onClick={() => onOpenChange(false)} className="rounded-sm border-[#025979] text-[#025979] hover:bg-[#E0F2FE]">Cancel</Button>
          <Button data-testid="group-save-btn" onClick={save} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm">Save Group</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Read-only summary row for a group --------------------------------------
function GroupCard({ group, typeLabels, readOnly, onEdit, onRemove }) {
  const typeText = (group.warehouse_types || []).map((t) => typeLabels[t] || t).join(", ");
  const solutionText = group.current_solution === "Other WMS" && group.current_solution_name
    ? `Other WMS — ${group.current_solution_name}`
    : (group.current_solution || "—") + (group.current_version ? ` (v${group.current_version})` : "");
  return (
    <div data-testid={`warehouse-group-card-${group.id}`} className="rounded-sm border border-[#E5E7EB] bg-white p-4 hover:border-[#D1D5DB] transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Boxes className="w-4 h-4 text-[#025979]" strokeWidth={1.75} />
            <h4 className="text-sm font-semibold text-[#111827]" data-testid={`group-name-${group.id}`}>{group.name}</h4>
            <span data-testid={`group-scenario-badge-${group.id}`} className="inline-flex items-center rounded-full bg-[#EDE9FE] text-[#6E5BCE] border border-[#6E5BCE]/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
              {SCENARIO_LABEL[group.assessment_type] || group.assessment_type}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(group.sites || []).map((s) => (
              <span key={s} className="inline-flex items-center gap-1 rounded-full bg-[#F1F5F9] text-[#475569] px-2 py-0.5 text-xs">
                <MapPin className="w-3 h-3" strokeWidth={1.75} />{s}
              </span>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 text-[12px] text-[#4B5563]">
            <p><span className="text-[#9CA3AF]">Warehouses:</span> {group.warehouse_count}</p>
            <p className="sm:col-span-2"><span className="text-[#9CA3AF]">Types:</span> {typeText || "—"}</p>
            <p className="sm:col-span-3"><span className="text-[#9CA3AF]">Current solution:</span> {solutionText}</p>
            {needsArch(group.assessment_type) && group.context?.preserve_architecture_importance && (
              <p className="sm:col-span-3"><span className="text-[#9CA3AF]">Preserve architecture:</span> {group.context.preserve_architecture_importance}</p>
            )}
          </div>
        </div>
        {!readOnly && (
          <div className="flex shrink-0 gap-1">
            <button data-testid={`edit-group-${group.id}`} onClick={() => onEdit(group)} className="p-2 rounded-sm text-[#025979] hover:bg-[#E0F2FE]" aria-label="Edit group">
              <Pencil className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <button data-testid={`remove-group-${group.id}`} onClick={() => onRemove(group)} className="p-2 rounded-sm text-red-600 hover:bg-red-50" aria-label="Remove group">
              <Trash2 className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function WarehouseGroups({ groups = [], onChange, warehouseTypeOptions = [], solutionOptions = [], preserveArchOptions = [], step1Scenario = "", readOnly = false, error = "" }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const defaultScenario = ["greenfield", "brownfield", "cloud_migration"].includes(step1Scenario)
    ? step1Scenario : "";
  const typeLabels = useMemo(
    () => Object.fromEntries((warehouseTypeOptions || []).map((o) => [o.value, o.label])),
    [warehouseTypeOptions]
  );

  const openAdd = () => { setEditing(emptyGroup(defaultScenario)); setEditorOpen(true); };
  const openEdit = (g) => { setEditing({ ...g }); setEditorOpen(true); };

  const handleSave = (group) => {
    const exists = groups.some((x) => x.id === group.id);
    const next = exists ? groups.map((x) => (x.id === group.id ? group : x)) : [...groups, group];
    onChange(next);
    setEditorOpen(false);
  };

  const confirmDelete = () => {
    if (pendingDelete) onChange(groups.filter((x) => x.id !== pendingDelete.id));
    setPendingDelete(null);
  };

  return (
    <div data-testid="warehouse-groups-section">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-[#025979]/10 flex items-center justify-center">
            <Boxes className="w-2.5 h-2.5 text-[#025979]" strokeWidth={2} />
          </div>
          <h2 className="text-sm font-semibold text-[#111827] uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>
            Warehouse Evaluation Groups
          </h2>
        </div>
        {!readOnly && (
          <Button data-testid="add-warehouse-group-btn" onClick={openAdd} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm h-9 px-3 text-xs font-semibold">
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} /> Add Warehouse Group
          </Button>
        )}
      </div>
      <p className="text-xs text-[#6B7280] mb-4 max-w-2xl">
        Group warehouses that can reasonably share one common set of assessment answers. Groups may represent individual sites, regions, systems, operating models, or transformation waves.
      </p>

      {readOnly && (
        <p data-testid="groups-readonly-note" className="text-[11px] text-[#025979] mb-3">Contributor view — Warehouse Groups are read-only.</p>
      )}

      {groups.length === 0 ? (
        <div data-testid="groups-empty-state" className="rounded-sm border border-dashed border-[#D1D5DB] bg-[#F8FAFC] p-6 text-center">
          <Boxes className="w-6 h-6 text-[#9CA3AF] mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-[#6B7280]">{readOnly ? "No warehouse groups have been defined." : "No warehouse groups yet. Add one to describe how you want to evaluate your warehouses."}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="warehouse-groups-list">
          {groups.map((g) => (
            <GroupCard key={g.id} group={g} typeLabels={typeLabels} readOnly={readOnly} onEdit={openEdit} onRemove={setPendingDelete} />
          ))}
        </div>
      )}

      {error && <p data-testid="warehouse-groups-error" className="text-xs text-red-500 mt-3">{error}</p>}

      {editing && (
        <GroupEditorDialog
          key={editing.id}
          open={editorOpen}
          onOpenChange={setEditorOpen}
          initial={editing}
          defaultScenario={defaultScenario}
          topScenario={step1Scenario}
          warehouseTypeOptions={warehouseTypeOptions}
          solutionOptions={solutionOptions}
          preserveArchOptions={preserveArchOptions}
          onSave={handleSave}
        />
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-group-dialog" className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this warehouse group?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingDelete?.name}" will be removed from this evaluation. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-group-cancel" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-group-confirm" onClick={confirmDelete} className="rounded-sm bg-red-600 hover:bg-red-700">Remove Group</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default WarehouseGroups;
